import type {
  AccountHealthLevel,
  AccountHealthPillar,
} from '../types/comercialAccountHealth'

/** Soma dos pontos das opções selecionadas (mapa pillarId → optionId). */
export function sumSelectedPoints(
  pillars: AccountHealthPillar[],
  selectedOptionByPillarId: Record<string, string>
): number {
  let total = 0
  for (const pillar of pillars) {
    if (!pillar.is_active) continue
    const optionId = selectedOptionByPillarId[pillar.id]
    if (!optionId) continue
    const option = pillar.options.find((o) => o.id === optionId && o.is_active)
    if (option) total += option.points
  }
  return total
}

/** Resolve o status a partir da pontuação total e das faixas configuradas. */
export function scoreToLevel(
  total: number,
  levels: AccountHealthLevel[]
): AccountHealthLevel | null {
  if (!Number.isFinite(total) || levels.length === 0) return null
  const sorted = [...levels].sort((a, b) => a.sort_order - b.sort_order)
  const match = sorted.find((level) => total >= level.min_score && total <= level.max_score)
  return match ?? null
}

export function sumPillarMaxPoints(pillars: AccountHealthPillar[]): number {
  return pillars.filter((p) => p.is_active).reduce((acc, p) => acc + p.max_points, 0)
}

/** Verifica só sobreposição (permite lacunas ao criar/ajustar faixas). */
export function validateLevelNoOverlap(levels: AccountHealthLevel[]): string | null {
  if (levels.length === 0) return null
  const sorted = [...levels].sort((a, b) => a.min_score - b.min_score)
  for (const level of sorted) {
    if (level.min_score > level.max_score) {
      return `Faixa "${level.label}" inválida: mínimo maior que máximo.`
    }
  }
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    if (curr.min_score <= prev.max_score) {
      return `Faixas "${prev.label}" e "${curr.label}" se sobrepõem.`
    }
  }
  return null
}

/** Verifica sobreposição + cobertura completa 0–100 (aviso na UI). */
export function validateLevelRanges(levels: AccountHealthLevel[]): string | null {
  if (levels.length === 0) return 'Nenhuma faixa de status cadastrada.'
  const overlap = validateLevelNoOverlap(levels)
  if (overlap) return overlap
  const sorted = [...levels].sort((a, b) => a.min_score - b.min_score)
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    if (curr.min_score !== prev.max_score + 1) {
      return `Há lacuna entre "${prev.label}" e "${curr.label}".`
    }
  }
  if (sorted[0].min_score !== 0) {
    return 'A faixa mais baixa deve começar em 0.'
  }
  if (sorted[sorted.length - 1].max_score !== 100) {
    return 'A faixa mais alta deve terminar em 100.'
  }
  return null
}
